import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useData } from '../contexts/DataContext';
import { useSettings } from '../contexts/SettingsContext';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Users, DollarSign, Clock, ArrowRight, Plus, Edit2, Trash2, ChevronLeft, ChevronRight, ChevronDown, Calendar, Upload, Download, FileSpreadsheet, X, Check, CheckCircle, AlertCircle, RotateCcw, Archive } from 'lucide-react';
import LeadFormModal from '../components/LeadFormModal';
import StarRating from '../components/StarRating';
import { toast } from 'sonner';
import * as api from '../services/api';
import InfoTooltip from '../components/ui/InfoTooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import * as XLSX from 'xlsx';

// Period options
const PERIOD_OPTIONS = [
  { value: 'week', label: 'Esta Semana' },
  { value: 'month', label: 'Este Mes' },
  { value: 'quarter', label: 'Este Trimestre' },
  { value: 'semester', label: 'Este Semestre' },
  { value: 'year', label: 'Este Año' },
  { value: 'all', label: 'Todo el Tiempo' },
  { value: 'custom', label: 'Rango Personalizado' },
];

const AllLeads = () => {
  const { state, actions } = useData();
  const { settings } = useSettings();
  const navigate = useNavigate();
  
  // Get current user and role
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'Administrador';
  const isViewer = currentUser?.role === 'viewer';
  const isTrafficker = currentUser?.role === 'trafficker';
  const canEditLeads = isAdmin || (!isViewer && !isTrafficker && currentUser?.permissions?.editLeads !== false);
  const canDeleteLeads = isAdmin; // Only admins can delete
  
  // Dynamic primary color
  const primaryColor = settings?.primaryColor || '#00b30c';
  
  // Restore filters from sessionStorage
  const [leads, setLeads] = useState([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [currentPage, setCurrentPage] = useState(() => parseInt(sessionStorage.getItem('leads_page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  
  const [searchTerm, setSearchTerm] = useState(() => sessionStorage.getItem('leads_search') || '');
  const [filterPipeline, setFilterPipeline] = useState(() => sessionStorage.getItem('leads_pipeline') || 'all');
  const [filterStage, setFilterStage] = useState(() => {
    const saved = sessionStorage.getItem('leads_stage');
    if (!saved || saved === 'all') return [];
    try { return JSON.parse(saved); } catch { return []; }
  });
  const [filterPeriod, setFilterPeriod] = useState(() => sessionStorage.getItem('leads_period') || 'all');
  const [filterTag, setFilterTag] = useState(() => sessionStorage.getItem('leads_tag') || 'all');
  const [filterAssigned, setFilterAssigned] = useState(() => sessionStorage.getItem('leads_assigned') || 'all');
  const [availableTags, setAvailableTags] = useState([]);
  const [comercialUsers, setComercialUsers] = useState([]);
  const [stageDropdownOpen, setStageDropdownOpen] = useState(false);
  const [viewingTrash, setViewingTrash] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const stageRef = useRef(null);

  // Close stage dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (stageRef.current && !stageRef.current.contains(e.target)) setStageDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const [customDateFrom, setCustomDateFrom] = useState(() => sessionStorage.getItem('leads_from') || '');
  const [customDateTo, setCustomDateTo] = useState(() => sessionStorage.getItem('leads_to') || '');
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [deletingLead, setDeletingLead] = useState(null);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [bulkImportData, setBulkImportData] = useState([]);
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState(null);
  const [bulkImportPipeline, setBulkImportPipeline] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [initialLoad, setInitialLoad] = useState(true);

  const ITEMS_PER_PAGE = 10;

  // Calculate period dates
  const getPeriodDates = () => {
    const now = new Date();
    let startDate;
    
    switch (filterPeriod) {
      case 'week':
        const dayOfWeek = now.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startDate = new Date(now);
        startDate.setDate(now.getDate() - daysToMonday);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      case 'semester':
        const semester = now.getMonth() < 6 ? 0 : 6;
        startDate = new Date(now.getFullYear(), semester, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      case 'all':
        return { startDate: null, endDate: null };
      case 'custom':
        return {
          startDate: customDateFrom ? new Date(customDateFrom) : null,
          endDate: customDateTo ? new Date(customDateTo + 'T23:59:59') : now
        };
      default:
        return { startDate: null, endDate: null };
    }
    
    return { startDate, endDate: now };
  };

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const searchInitialized = useRef(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      if (searchInitialized.current) {
        setCurrentPage(1);
      }
      searchInitialized.current = true;
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load leads with pagination - using API directly to avoid dependency issues
  useEffect(() => {
    api.getLeadTags().then(setAvailableTags).catch(() => {});
    api.getUsers().then(users => setComercialUsers(users.filter(u => ['comercial', 'admin', 'asistente'].includes(u.role) && u.status === 'active'))).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    
    const fetchLeads = async () => {
      // Only show loading spinner on initial load
      if (initialLoad) {
        setLoading(true);
      }
      try {
        if (viewingTrash) {
          const data = await api.getTrashedLeads(currentPage, ITEMS_PER_PAGE);
          if (!cancelled) {
            setLeads(data.leads || []);
            setTotalLeads(data.total || 0);
            setTotalPages(data.pages || 1);
          }
        } else {
        const pipelineId = filterPipeline === 'all' ? null : filterPipeline;
        const stageId = filterStage.length > 0 ? filterStage.join(',') : null;
        const tagId = filterTag === 'all' ? null : filterTag;
        const assignedTo = filterAssigned === 'all' ? null : filterAssigned;
        const search = debouncedSearch || null;
        const { startDate, endDate } = getPeriodDates();
        
        const data = await api.getLeads(
          currentPage, 
          ITEMS_PER_PAGE, 
          pipelineId, 
          search,
          startDate?.toISOString(),
          endDate?.toISOString(),
          stageId,
          tagId,
          assignedTo
        );
        
        if (!cancelled) {
          setLeads(data.leads || []);
          setTotalLeads(data.total || 0);
          setTotalPages(data.pages || 1);
        }
        }
      } catch (error) {
        console.error('Error loading leads:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setInitialLoad(false);
        }
      }
    };
    
    fetchLeads();
    
    return () => {
      cancelled = true;
    };
  }, [currentPage, filterPipeline, filterStage, filterTag, filterAssigned, debouncedSearch, filterPeriod, customDateFrom, customDateTo, initialLoad, viewingTrash]);

  // Persist filters to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('leads_page', String(currentPage));
    sessionStorage.setItem('leads_pipeline', filterPipeline);
    sessionStorage.setItem('leads_stage', JSON.stringify(filterStage));
    sessionStorage.setItem('leads_period', filterPeriod);
    sessionStorage.setItem('leads_tag', filterTag);
    sessionStorage.setItem('leads_assigned', filterAssigned);
    sessionStorage.setItem('leads_search', searchTerm);
    sessionStorage.setItem('leads_from', customDateFrom);
    sessionStorage.setItem('leads_to', customDateTo);
  }, [currentPage, filterPipeline, filterStage, filterPeriod, filterTag, searchTerm, customDateFrom, customDateTo]);

  // Reset page when filter changes (skip on initial render to preserve URL state)
  const hasUserInteracted = useRef(false);
  useEffect(() => {
    if (!hasUserInteracted.current) {
      return;
    }
    setCurrentPage(1);
  }, [filterPipeline, filterStage, filterPeriod]);

  const reloadLeads = async () => {
    try {
      const pipelineId = filterPipeline === 'all' ? null : filterPipeline;
      const stageId = filterStage === 'all' ? null : filterStage;
      const tagId = filterTag === 'all' ? null : filterTag;
      const assignedTo = filterAssigned === 'all' ? null : filterAssigned;
      const search = debouncedSearch || null;
      const { startDate, endDate } = getPeriodDates();
      
      const data = await api.getLeads(
        currentPage, 
        ITEMS_PER_PAGE, 
        pipelineId, 
        search,
        startDate?.toISOString(),
        endDate?.toISOString(),
        stageId,
        tagId,
        assignedTo
      );
      setLeads(data.leads || []);
      setTotalLeads(data.total || 0);
      setTotalPages(data.pages || 1);
    } catch (error) {
      console.error('Error reloading leads:', error);
    }
  };

  const handleTrashLead = async (e, leadId) => {
    if (e) e.stopPropagation();
    try {
      await api.trashLead(leadId);
      toast.success('Lead movido a papelera');
      reloadLeads();
    } catch { toast.error('Error al mover a papelera'); }
  };

  const handleRestoreLead = async (e, leadId) => {
    if (e) e.stopPropagation();
    try {
      await api.restoreLead(leadId);
      toast.success('Lead restaurado');
      reloadLeads();
    } catch { toast.error('Error al restaurar'); }
  };

  const handleBulkTrash = async () => {
    if (selectedLeads.length === 0) return;
    if (!window.confirm(`¿Mover ${selectedLeads.length} lead(s) a papelera?`)) return;
    try {
      await api.bulkTrashLeads(selectedLeads);
      toast.success(`${selectedLeads.length} leads movidos a papelera`);
      setSelectedLeads([]);
      reloadLeads();
    } catch { toast.error('Error al mover a papelera'); }
  };


  const totalValue = leads.reduce((sum, lead) => sum + (lead.dealsTotalValue || 0), 0);

  // Get currency from settings
  const currency = settings?.currency || 'COP';

  // Handle rating update
  const handleRatingUpdate = async (e, leadId, rating) => {
    if (e) e.stopPropagation();
    try {
      await api.updateLeadRating(leadId, rating);
      // Update local state
      setLeads(prev => prev.map(lead => 
        lead.id === leadId ? { ...lead, rating } : lead
      ));
    } catch (error) {
      console.error('Error updating rating:', error);
      toast.error('Error al actualizar calificación');
    }
  };

  const formatValue = (value) => {
    if (currency === 'COP') {
      return new Intl.NumberFormat('es-CO', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value) + ' COP';
    }
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPriorityColor = (priority) => {
    const colors = {
      Alta: 'bg-red-100 text-red-700',
      Media: 'bg-orange-100 text-orange-700',
      Baja: 'bg-green-100 text-green-700',
    };
    return colors[priority] || 'bg-slate-100 text-slate-700';
  };

  const handleEditLead = (e, lead) => {
    e.stopPropagation();
    setEditingLead(lead);
    setShowLeadModal(true);
  };

  const handleDeleteClick = (e, lead) => {
    e.stopPropagation();
    setDeletingLead(lead);
  };

  const handleConfirmDelete = async () => {
    if (!deletingLead) return;
    
    try {
      await actions.deleteLead(deletingLead.id);
      toast.success(`Lead "${deletingLead.name}" eliminado`);
      setDeletingLead(null);
      reloadLeads();
    } catch (error) {
      toast.error('Error al eliminar el lead');
    }
  };

  const handleCloseModal = () => {
    setShowLeadModal(false);
    setEditingLead(null);
    reloadLeads();
  };

  // Bulk Import Functions

  const handleBulkImport = async () => {
    if (bulkImportData.length === 0) {
      toast.error('No hay datos para importar');
      return;
    }
    
    if (!bulkImportPipeline) {
      toast.error('Por favor seleccione un embudo de destino');
      return;
    }
    
    // Get the first stage of the selected pipeline
    const selectedPipeline = state.pipelines.find(p => p.id === bulkImportPipeline);
    const firstStage = selectedPipeline?.stages?.[0]?.id || 'nuevo-lead';
    
    setBulkImportLoading(true);
    try {
      // Add pipeline and stage to each lead
      const leadsWithPipeline = bulkImportData.map(lead => ({
        ...lead,
        pipelineId: bulkImportPipeline,
        stageId: firstStage
      }));
      
      const result = await api.bulkImportLeads(leadsWithPipeline);
      setBulkImportResult(result);
      if (result.imported > 0) {
        toast.success(result.message);
        reloadLeads();
      }
    } catch (error) {
      toast.error('Error al importar leads');
    } finally {
      setBulkImportLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        processFile(file);
      } else {
        toast.error('Por favor suba un archivo Excel (.xlsx o .xls)');
      }
    }
  };

  const processFile = (file) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        const mappedData = jsonData.map(row => ({
          name: row.nombre || row.name || '',
          email: row.email || row.correo || '',
          phone: row.telefono || row.phone || '',
          company: row.empresa || row.company || '',
          source: row.fuente || row.source || 'Carga Masiva',
          priority: row.prioridad || row.priority || 'Media',
          notes: row.notas || row.notes || ''
        })).filter(lead => lead.name);
        
        if (mappedData.length === 0) {
          toast.error('No se encontraron datos válidos en el archivo');
          return;
        }
        
        setBulkImportData(mappedData);
        setBulkImportResult(null);
        toast.success(`${mappedData.length} leads cargados del archivo`);
      } catch (error) {
        console.error('Error processing file:', error);
        toast.error('Error al procesar el archivo');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      { nombre: 'Juan Pérez', email: 'juan@ejemplo.com', telefono: '+57 300 123 4567', empresa: 'Empresa ABC', fuente: 'Web', prioridad: 'Alta', notas: 'Interesado en servicio premium' },
      { nombre: 'María García', email: 'maria@ejemplo.com', telefono: '+57 311 987 6543', empresa: 'Comercial XYZ', fuente: 'Referido', prioridad: 'Media', notas: '' },
      { nombre: 'Carlos López', email: 'carlos@ejemplo.com', telefono: '+57 320 555 1234', empresa: 'Tech Solutions', fuente: 'Evento', prioridad: 'Alta', notas: 'Conoció el producto en feria' }
    ];
    
    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Leads');
    XLSX.writeFile(wb, 'plantilla_leads.xlsx');
    toast.success('Plantilla descargada');
  };

  const closeBulkImportModal = () => {
    setShowBulkImportModal(false);
    setBulkImportData([]);
    setBulkImportResult(null);
    setBulkImportPipeline('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };


  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-2">
              {viewingTrash ? 'Papelera de Leads' : 'Todos los Leads'}
            </h1>
            <p className="text-lg text-slate-500">
              {viewingTrash ? 'Leads descartados que no se cuentan en estadísticas' : 'Gestiona y filtra todos tus leads'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setViewingTrash(!viewingTrash); setCurrentPage(1); setSelectedLeads([]); }}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-all ${viewingTrash ? 'bg-amber-100 text-amber-700 border border-amber-300' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              data-testid="trash-toggle-btn"
            >
              <Archive className="w-4 h-4" />
              {viewingTrash ? 'Ver Leads Activos' : 'Papelera'}
            </button>
            {!viewingTrash && selectedLeads.length > 0 && (
              <button onClick={handleBulkTrash}
                className="px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium bg-red-50 text-red-600 border border-red-200 hover:bg-red-100">
                <Trash2 className="w-4 h-4" />
                Descartar ({selectedLeads.length})
              </button>
            )}
            {canEditLeads && !viewingTrash && (
            <button
              onClick={() => setShowBulkImportModal(true)}
              className="border border-slate-200 text-slate-700 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-slate-50 transition-all"
            >
              <Upload className="w-4 h-4" />
              Carga en Lote
            </button>
          )}
          {canEditLeads && (
            <button
              onClick={() => {
                setEditingLead(null);
                setShowLeadModal(true);
              }}
              className="hover:opacity-90 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all"
              style={{ backgroundColor: primaryColor }}
            >
              <Plus className="w-4 h-4" />
              Nuevo Lead
            </button>
          )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="bg-blue-50 p-2 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500 flex items-center gap-1">
                Total de Leads
                <InfoTooltip content="Número total de leads que coinciden con los filtros seleccionados (embudo, período, búsqueda). Se cuenta por fecha de creación del lead." />
              </p>
              <p className="text-xl font-bold text-slate-900">{totalLeads.toLocaleString('es-ES')}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="bg-green-50 p-2 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500 flex items-center gap-1">
                Valor en Página
                <InfoTooltip content="Suma del valor estimado de los leads mostrados en la página actual. No incluye todos los leads, solo los visibles." />
              </p>
              <p className="text-xl font-bold text-green-600">{formatValue(totalValue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por nombre, email, cédula, campaña..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <select
            value={filterPipeline}
            onChange={(e) => { hasUserInteracted.current = true; setFilterPipeline(e.target.value); setFilterStage([]); }}
            className="pl-10 pr-8 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 appearance-none bg-white"
          >
            <option value="all">Todos los Embudos</option>
            {state.pipelines.map(pipeline => (
              <option key={pipeline.id} value={pipeline.id}>{pipeline.name}</option>
            ))}
          </select>
        </div>
        <div className="relative" ref={stageRef}>
          <button
            onClick={() => setStageDropdownOpen(!stageDropdownOpen)}
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 text-sm min-w-[160px]"
            data-testid="filter-stage"
          >
            <span className="truncate">
              {filterStage.length === 0 ? 'Todas las Etapas' : `${filterStage.length} etapa${filterStage.length > 1 ? 's' : ''}`}
            </span>
            <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${stageDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          {stageDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-xl z-50 max-h-72 overflow-y-auto">
              <button onClick={() => { setFilterStage([]); hasUserInteracted.current = true; }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${filterStage.length === 0 ? 'font-semibold text-indigo-600 bg-indigo-50' : 'text-slate-700'}`}>
                Todas las Etapas
              </button>
              <div className="border-t border-slate-100" />
              {(() => {
                const stages = filterPipeline === 'all'
                  ? state.pipelines.flatMap(p => p.stages || [])
                  : (state.pipelines.find(p => p.id === filterPipeline)?.stages || []);
                const unique = [];
                const seen = new Set();
                stages.forEach(s => { if (!seen.has(s.name)) { seen.add(s.name); unique.push(s); } });
                unique.sort((a, b) => a.name.localeCompare(b.name));
                return unique.map(stage => {
                  const isSelected = filterStage.includes(stage.id);
                  return (
                    <button key={stage.id} onClick={() => {
                      hasUserInteracted.current = true;
                      setFilterStage(prev => isSelected ? prev.filter(s => s !== stage.id) : [...prev, stage.id]);
                    }}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-slate-50 ${isSelected ? 'bg-indigo-50' : ''}`}>
                      <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'}`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className={isSelected ? 'font-medium text-indigo-700' : 'text-slate-700'}>{stage.name}</span>
                    </button>
                  );
                });
              })()}
            </div>
          )}
        </div>
        {availableTags.length > 0 && (
          <div className="relative">
            <select
              value={filterTag}
              onChange={(e) => { hasUserInteracted.current = true; setFilterTag(e.target.value); }}
              className="px-4 pr-8 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 appearance-none bg-white"
              data-testid="filter-tag"
            >
              <option value="all">Todas las Etiquetas</option>
              {availableTags.map(tag => (
                <option key={tag.id} value={tag.id}>{tag.name}</option>
              ))}
            </select>
          </div>
        )}
        {comercialUsers.length > 0 && (
          <div className="relative">
            <select
              value={filterAssigned}
              onChange={(e) => { hasUserInteracted.current = true; setFilterAssigned(e.target.value); }}
              className="px-4 pr-8 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 appearance-none bg-white"
              data-testid="filter-assigned"
            >
              <option value="all">Todos los Comerciales</option>
              {comercialUsers.map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <select
            value={filterPeriod}
            onChange={(e) => { hasUserInteracted.current = true; setFilterPeriod(e.target.value); }}
            className="pl-10 pr-8 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 appearance-none bg-white"
          >
            {PERIOD_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        {filterPeriod === 'custom' && (
          <>
            <input
              type="date"
              value={customDateFrom}
              onChange={(e) => setCustomDateFrom(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
            />
            <input
              type="date"
              value={customDateTo}
              onChange={(e) => setCustomDateTo(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
            />
          </>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : leads.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No se encontraron leads</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    {!viewingTrash && (
                      <th className="w-10 px-3 py-3">
                        <input type="checkbox"
                          checked={selectedLeads.length > 0 && selectedLeads.length === leads.length}
                          onChange={(e) => setSelectedLeads(e.target.checked ? leads.map(l => l.id) : [])}
                          className="w-4 h-4 rounded border-slate-300" />
                      </th>
                    )}
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Nombre</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Empresa</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Embudo</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Etapa</th>
                    <th className="text-center px-6 py-3 text-sm font-semibold text-slate-600">Calificación</th>
                    <th className="text-right px-6 py-3 text-sm font-semibold text-slate-600">Valor Negocios</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Prioridad</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-slate-600">Fecha</th>
                    <th className="text-right px-6 py-3 text-sm font-semibold text-slate-600">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {leads.map((lead) => {
                    const pipeline = state.pipelines.find(p => p.id === lead.pipelineId);
                    const stage = pipeline?.stages?.find(s => s.id === lead.stageId);

                    return (
                      <tr
                        key={lead.id}
                        onClick={() => navigate(`/lead/${lead.id}`)}
                        className="hover:bg-slate-50 transition-colors cursor-pointer"
                      >
                        {!viewingTrash && (
                          <td className="w-10 px-3 py-4" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox"
                              checked={selectedLeads.includes(lead.id)}
                              onChange={(e) => setSelectedLeads(prev => e.target.checked ? [...prev, lead.id] : prev.filter(id => id !== lead.id))}
                              className="w-4 h-4 rounded border-slate-300" />
                          </td>
                        )}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-indigo-600 font-semibold text-sm">
                                {lead.name?.charAt(0).toUpperCase() || 'L'}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-900 truncate">{lead.name}</p>
                              <p className="text-sm text-slate-500 truncate">{lead.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-slate-600 truncate block max-w-[150px]">
                            {lead.company || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-slate-600">{pipeline?.name || '-'}</span>
                        </td>
                        <td className="px-6 py-4">
                          {stage && (
                            <span
                              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                              style={{ backgroundColor: `${stage.color}20`, color: stage.color }}
                            >
                              {stage.name}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div onClick={(e) => e.stopPropagation()}>
                            <StarRating 
                              rating={lead.rating || 0} 
                              onRate={(rating) => handleRatingUpdate(null, lead.id, rating)}
                              size="sm"
                            />
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="font-semibold text-green-600">
                            {formatValue(lead.dealsTotalValue || 0)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getPriorityColor(lead.priority)}`}>
                            {lead.priority || 'Media'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-slate-500">{formatDate(lead.createdAt)}</span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {viewingTrash ? (
                              <button
                                onClick={(e) => handleRestoreLead(e, lead.id)}
                                className="p-2 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                                title="Restaurar lead"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </button>
                            ) : (
                              <>
                                {canEditLeads && (
                                  <button
                                    onClick={(e) => handleEditLead(e, lead)}
                                    className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                    title="Editar lead"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  onClick={(e) => handleTrashLead(e, lead.id)}
                                  className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                  title="Mover a papelera"
                                >
                                  <Archive className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
              <div className="text-sm text-slate-600">
                Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalLeads)} de {totalLeads.toLocaleString('es-ES')} leads
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                
                {getPageNumbers().map(page => (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      page === currentPage
                        ? 'bg-indigo-600 text-white'
                        : 'hover:bg-white border border-slate-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Lead Form Modal */}
      <LeadFormModal
        isOpen={showLeadModal}
        onClose={handleCloseModal}
        leadToEdit={editingLead}
      />

      {/* Delete Confirmation Modal */}
      {deletingLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4 p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="bg-red-100 p-3 rounded-full">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Eliminar Lead
                </h3>
                <p className="text-sm text-slate-500">
                  Esta acción no se puede deshacer
                </p>
              </div>
            </div>
            
            <p className="text-slate-700 mb-6">
              ¿Estás seguro de que deseas eliminar a <strong>{deletingLead.name}</strong>? 
              Se eliminarán también todas las actividades, archivos y alertas asociadas.
            </p>
            
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeletingLead(null)}
                className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      <Dialog open={showBulkImportModal} onOpenChange={setShowBulkImportModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
              Carga en Lote de Leads
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Instructions */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <h4 className="font-medium text-emerald-900 mb-2">Instrucciones:</h4>
              <ol className="text-sm text-emerald-800 space-y-1 list-decimal list-inside">
                <li>Descargue la plantilla de ejemplo</li>
                <li>Complete los datos de los leads en el archivo Excel</li>
                <li>Seleccione el embudo de destino</li>
                <li>Suba el archivo y revise los datos</li>
                <li>Confirme la importación</li>
              </ol>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Template Download */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Plantilla de ejemplo</p>
                  <p className="text-sm text-slate-500">Archivo Excel con formato</p>
                </div>
                <Button onClick={downloadTemplate} variant="outline" size="sm" className="flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Descargar
                </Button>
              </div>

              {/* Pipeline Selector */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <label className="block font-medium text-slate-900 mb-2">
                  Embudo de destino <span className="text-red-500">*</span>
                </label>
                <select
                  value={bulkImportPipeline}
                  onChange={(e) => setBulkImportPipeline(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                  required
                >
                  <option value="">Seleccione un embudo...</option>
                  {state.pipelines?.map(pipeline => (
                    <option key={pipeline.id} value={pipeline.id}>
                      {pipeline.name} ({pipeline.stages?.length || 0} etapas)
                    </option>
                  ))}
                </select>
                {bulkImportPipeline && (
                  <p className="text-xs text-emerald-600 mt-1">
                    Los leads se crearán en la primera etapa del embudo
                  </p>
                )}
              </div>
            </div>

            {/* Column Info - Compact */}
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="font-medium text-slate-900 mb-2 text-sm">Columnas del archivo:</p>
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div><span className="font-medium text-red-600">nombre*</span></div>
                <div><span className="text-slate-600">email</span></div>
                <div><span className="text-slate-600">telefono</span></div>
                <div><span className="text-slate-600">empresa</span></div>
                <div><span className="text-slate-600">fuente</span></div>
                <div><span className="text-slate-600">prioridad</span></div>
                <div><span className="text-slate-600">notas</span></div>
              </div>
            </div>

            {/* File Upload with Drag & Drop */}
            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                isDragging 
                  ? 'border-emerald-500 bg-emerald-50' 
                  : 'border-slate-300 hover:border-emerald-400'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
                id="bulk-file-input"
              />
              <label htmlFor="bulk-file-input" className="cursor-pointer block">
                <Upload className={`w-10 h-10 mx-auto mb-3 ${isDragging ? 'text-emerald-500' : 'text-slate-400'}`} />
                <p className="font-medium text-slate-700">
                  {isDragging ? 'Suelte el archivo aquí' : 'Arrastre y suelte o haga clic para seleccionar'}
                </p>
                <p className="text-sm text-slate-500">Archivos .xlsx o .xls</p>
              </label>
            </div>

            {/* Preview Data */}
            {bulkImportData.length > 0 && !bulkImportResult && (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-emerald-50 px-4 py-2 border-b border-slate-200 flex items-center justify-between">
                  <p className="font-medium text-emerald-900">{bulkImportData.length} leads listos para importar</p>
                  <button 
                    onClick={() => { setBulkImportData([]); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    className="text-sm text-slate-500 hover:text-red-600"
                  >
                    Limpiar
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">Nombre</th>
                        <th className="text-left px-3 py-2">Email</th>
                        <th className="text-left px-3 py-2">Teléfono</th>
                        <th className="text-left px-3 py-2">Empresa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkImportData.slice(0, 10).map((lead, idx) => (
                        <tr key={idx} className="border-t border-slate-100">
                          <td className="px-3 py-2 font-medium">{lead.name}</td>
                          <td className="px-3 py-2 text-slate-500">{lead.email}</td>
                          <td className="px-3 py-2 text-slate-500">{lead.phone}</td>
                          <td className="px-3 py-2 text-slate-500">{lead.company}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {bulkImportData.length > 10 && (
                    <p className="text-center text-sm text-slate-500 py-2">
                      ... y {bulkImportData.length - 10} más
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Import Result */}
            {bulkImportResult && (
              <div className={`p-4 rounded-lg ${bulkImportResult.imported > 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {bulkImportResult.imported > 0 ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  )}
                  <p className="font-medium">{bulkImportResult.message}</p>
                </div>
                {bulkImportResult.errors?.length > 0 && (
                  <div className="mt-2 text-sm text-red-700">
                    <p className="font-medium">Errores:</p>
                    <ul className="list-disc list-inside">
                      {bulkImportResult.errors.slice(0, 5).map((err, idx) => (
                        <li key={idx}>Fila {err.row}: {err.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button variant="outline" onClick={closeBulkImportModal}>
                {bulkImportResult ? 'Cerrar' : 'Cancelar'}
              </Button>
              {!bulkImportResult && bulkImportData.length > 0 && (
                <Button 
                  onClick={handleBulkImport} 
                  disabled={bulkImportLoading || !bulkImportPipeline}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {bulkImportLoading ? 'Importando...' : `Importar ${bulkImportData.length} Leads`}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllLeads;
